import React, { useMemo } from 'react';
import {
  ListOfVehicles,
  VehicleData,
  CardContentWrapper,
  CardWrapper,
  ViewAllResults,
  NoResultsHeader,
} from './SidewideSearch.styles';
import { Card, Headline, Icon, Text } from '@oneaudi/unified-web-components';
import type { InventoryGroup } from '../../../types';
import { HOST_NAME } from '../../constants';
import CPOLogo from '../../assets/CpoBadge';
import VehicleCard from './VehicleCard';

interface VehicleListProps {
  vehiclesData: InventoryGroup | undefined;
  type: 'NEW' | 'USED' | 'CPO';
  showPrice: boolean;
  language?: string;
  market?: string;
}

const VehicleList: React.FC<VehicleListProps> = ({
  vehiclesData,
  type,
  showPrice,
  language,
  market,
}) => {
  const vehicles = vehiclesData?.vehicles;
  const totalCount = vehiclesData?.totalCount;
  const vlpLink = vehiclesData?.vlpLink;

  const vlpRedirectUrl = `${HOST_NAME}${vlpLink}`;
  const isCpoVehicles = type === 'CPO';
  const isUsedVehicle = type === 'USED';
  const isOneResult = totalCount === 1;

  const isFrench = language === 'fr';

  const hasTechnicalError = vehiclesData?.code && vehiclesData?.reason?.includes('error');

  const ErrorState = useMemo(() => {
    return (
      <NoResultsHeader>
        <Headline
          tag="h3"
          variant={{
            order: '3',
            type: 'headline',
            weight: 'normal',
          }}
        >
          {isFrench ? 'Les résultats n’ont pas pu être chargés.' : 'Results couldn’t be loaded.'}
        </Headline>
        <Text>
          {isFrench
            ? 'Veuillez réessayer ou consulter toutes les options disponibles sur nos pages de véhicules.'
            : 'Try again, or browse all available options on our vehicle listing pages.'}
        </Text>
      </NoResultsHeader>
    );
  }, [isFrench]);

  const vlpLabel = useMemo(() => {
    if (isUsedVehicle) return isFrench ? 'Véhicule d’occasion' : 'Pre-owned vehicle';
    if (isCpoVehicles)
      return isFrench
        ? 'Véhicule certifié :plus'
        : `Certified ${market !== 'us' ? ':plus' : 'Pre-owned'} vehicle`;
    return isFrench ? 'Véhicule neuf' : 'New vehicle';
  }, [isCpoVehicles, isUsedVehicle, isFrench, market]);

  const vlpRedirectCta = useMemo(() => {
    return (
      <CardWrapper tabIndex={0} href={vlpRedirectUrl} vlpCard>
        <Card margin={[0, 0, 0, 0]}>
          <CardContentWrapper>
            <Icon name="traffic" size="l" />
            <VehicleData gap={4} vlpCard>
              <Text>{vlpLabel}</Text>
              <ViewAllResults>
                <Text>
                  {isFrench ? 'Afficher' : 'View'} {isOneResult ? '' : isFrench ? 'les' : 'all'}{' '}
                  {totalCount}{' '}
                  {isOneResult
                    ? isFrench
                      ? 'résultat'
                      : 'result'
                    : isFrench
                      ? 'résultats'
                      : 'results'}
                </Text>
                <Icon name="forward" size="s" />
              </ViewAllResults>
            </VehicleData>
          </CardContentWrapper>
        </Card>
      </CardWrapper>
    );
  }, [isOneResult, totalCount, vlpLabel, vlpRedirectUrl, isFrench]);

  const logoType = market === 'us' ? 'CPO_USA' : isFrench ? 'CPO_CAN_FR' : 'CPO_CAN_EN';

  return (
    <ListOfVehicles>
      {isCpoVehicles && <CPOLogo type={logoType} />}
      {hasTechnicalError && ErrorState}
      {vehicles?.map((vehicle) => {
        return (
          <VehicleCard
            key={vehicle.id}
            vehicle={vehicle}
            showPrice={showPrice}
            type={type}
            language={language}
          />
        );
      })}
      {vlpRedirectCta}
    </ListOfVehicles>
  );
};

export default VehicleList;
